import { type FC } from 'react';
import { cva } from 'class-variance-authority';
import cn from 'classnames';

import {
  type ContractData,
  type PaidSpecContractType,
  type SpecContractType,
} from '@/common/types/contract';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';

import { CompleteOverlay } from '../CompleteOverlay/CompleteOverlay';
import { ContractCard } from '../ContractCard/ContractCard';
import { FailOverlay } from '../FailOverlay/FailOverlay';

import { ContractTermsPlug } from './ContractTermsPlug';

import css from './styles.module.scss';

const contractTermsVariant = cva('', {
  variants: {
    kind: {
      Grand: css.grandTerms,
      GrandPlus: css.grandTerms,
      Custom: css.customTerms,
      Golden: css.goldenTerms,
      GoldenPlus: css.goldenTerms,
      Blitz: css.blitzTerm,
      BlitzPlus: css.blitzTerm,
    },
  },
});

interface ContractTermsProps {
  kind: SpecContractType | PaidSpecContractType;
  data?: ContractData;
  openDate?: string;
  hasContractComplete: boolean;
  hasContractFail: boolean;
  hasContractExpired: boolean;
  className?: string;
  plug?: {
    cardsCount: number;
  };
}

const ContractTerms: FC<ContractTermsProps> = ({
  kind,
  openDate,
  className,
  data,
  hasContractComplete,
  hasContractFail,
  hasContractExpired,
  plug,
}) => {
  const pointsCount = data ? Object.keys(data).length : 0;

  return (
    <div className={cn(contractTermsVariant({ kind }), 'relative', className)}>
      {!!data &&
        Object.entries(data).map(([item, value], index) => (
          <ContractCard
            key={item}
            index={index}
            isOpen={!!openDate}
            item={item as ResourceTypeText | PlantTypeText | GoodsType}
            isHorizontal={pointsCount === 3}
            contractType={kind}
            inModal={true}
            value={value}
            className={cn(
              'w-[5.5rem] aspect-square',
              (hasContractFail || hasContractExpired) && 'opacity-50',
            )}
          />
        ))}
      {!data && plug && <ContractTermsPlug {...plug} />}
      {hasContractComplete && <CompleteOverlay />}
      {!(kind === 'Blitz' || kind === 'BlitzPlus') && (
        <FailOverlay kind={kind} />
      )}
    </div>
  );
};

export { ContractTerms };
